import { MutableRefObject, useEffect, useRef, useState } from 'react'

export const useOutsideClick = (onOutsideClick: () => void): MutableRefObject<HTMLDivElement> => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleOutsideClick = (ev: MouseEvent) => {
      if (!ref.current || ref.current.contains(ev.target as Node)) return
      removeEventLister()
      onOutsideClick()
    }
    const removeEventLister = () => document.removeEventListener('click', handleOutsideClick, false)
    document.addEventListener('click', handleOutsideClick, false)
    return removeEventLister
  }, [ref, onOutsideClick])

  return ref
}

export const useInputExtraPaddingLeft = (initialPaddingLeft: number = 15): {
  versionBeforeValueRef: MutableRefObject<HTMLDivElement>
  paddingLeft: number
} => {
  const versionBeforeValueRef = useRef<HTMLDivElement>(null)
  const [paddingLeft, setPaddingLeft] = useState<number>(initialPaddingLeft)

  useEffect(() => {
    if (versionBeforeValueRef.current?.clientWidth) {
      setPaddingLeft(versionBeforeValueRef.current?.clientWidth + initialPaddingLeft + 5)
    }
  }, [versionBeforeValueRef, initialPaddingLeft])

  return {
    versionBeforeValueRef,
    paddingLeft
  }
}

export const useGetScrollWidth = () => {
  const [scrollX, setScrollX] = useState<number | string>('100%')
  const lastScrollLeft = useRef<number>(0)

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const currentScrollLeft = e.currentTarget.scrollLeft
    if (currentScrollLeft !== lastScrollLeft.current) {
      setScrollX(e.currentTarget.scrollWidth)
      lastScrollLeft.current = currentScrollLeft
    }
  }

  return {
    scrollX,
    handleScroll
  }
}

export interface FrameConfig {
  rows?: number;
  cols?: number;
  spriteBaseUrl: string;
  maxSprites?: number;
}

export async function preloadFrames (spritePath:string, rows = 11, cols = 6, maxSprites = 5000) {
  const allFrames = []

  for (let i = 1; i <= maxSprites; i++) {
    const imageUrl = `${spritePath}/${i}.jpg`

    try {
      const image = await loadImage(imageUrl)
      if (!image || image.naturalWidth === 0) break

      const frames = extractFramesFromSprite(image, rows, cols)
      allFrames.push(...frames)
    } catch (e) {
      // Stop if image doesn't exist or load fails
      break
    }
  }

  return allFrames
}

function loadImage (src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

function extractFramesFromSprite (image: HTMLImageElement, rows: number, cols: number): HTMLCanvasElement[] {
  const frameWidth = image.width / cols
  const frameHeight = image.height / rows
  const frames: HTMLCanvasElement[] = []

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  canvas.width = image.width
  canvas.height = image.height
  ctx?.drawImage(image, 0, 0)

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const frameCanvas = document.createElement('canvas')
      frameCanvas.width = frameWidth
      frameCanvas.height = frameHeight

      const frameCtx = frameCanvas.getContext('2d')
      frameCtx?.drawImage(
        canvas,
        x * frameWidth,
        y * frameHeight,
        frameWidth,
        frameHeight,
        0,
        0,
        frameWidth,
        frameHeight
      )

      frames.push(frameCanvas)
    }
  }

  return frames
}

export const useGetIsResponsiveScreen = (maxWidth: string) => {
  const [isResponsive, setIsResponsive] = useState<boolean>(() =>
    window.matchMedia(maxWidth).matches
  )

  useEffect(() => {
    const mediaQuery = window.matchMedia(maxWidth)

    const handleChange = (event: MediaQueryListEvent) => {
      setIsResponsive(event.matches)
    }

    setIsResponsive(mediaQuery.matches)

    mediaQuery.addEventListener('change', handleChange)

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [maxWidth])

  return { isResponsive }
}
